import type { UpdatedContent } from '@mathflat/domain/Notice'
import { makeAutoObservable } from 'mobx'

import commonRepo from '@/repository/common.repo'
import { localStorageService } from '@/service/storage.service'

class UpdatedContentsService {
  private _updatedContents: UpdatedContent[] = []

  constructor() {
    makeAutoObservable(this)
  }

  setContents(data: UpdatedContent[]) {
    this._updatedContents = data.sort((a, b) => b.priority - a.priority)
  }

  get updatedContents() {
    return this._updatedContents
  }

  getUpdatedContent() {
    if (this._filteredUpdatedContents.length === 0) {
      return null
    }

    return this._filteredUpdatedContents[0]
  }

  private get _filteredUpdatedContents() {
    return this._updatedContents.filter(
      (updatedContent) =>
        this._filterByAcademyInfo(updatedContent) &&
        this._filterByDate(updatedContent) &&
        this._filterByStoredInfo(updatedContent),
    )
  }

  get hasNoDisplayableContents() {
    return this._updatedContents.length > 0 && this._filteredUpdatedContents.length === 0
  }

  private _filterByAcademyInfo(updatedContents: UpdatedContent) {
    if (!commonRepo.academy || !commonRepo.academy.academyId) return false

    const { dcodes, academyAuthority, academyStatus, academyType } = updatedContents

    if (dcodes && dcodes.split('\n').includes(commonRepo.academy.academyId)) return true

    return (
      academyAuthority[commonRepo.academy.authority] &&
      academyStatus[commonRepo.academy.status] &&
      academyType[commonRepo.academy.isB2G ? 'B2G' : 'B2B']
    )
  }

  private _filterByDate(updatedContents: UpdatedContent) {
    const { showStartDate, showEndDate } = updatedContents
    return new Date() >= new Date(showStartDate) && new Date() <= new Date(showEndDate)
  }

  private _filterByStoredInfo({ id, maxViewsPerDevice }: UpdatedContent) {
    if (!maxViewsPerDevice || isNaN(Number(maxViewsPerDevice))) {
      return true
    }

    const storedInfo = this.getStoredInfo(id)
    const showCount = storedInfo?.showCount ?? 0

    if (maxViewsPerDevice && maxViewsPerDevice > showCount) {
      return true
    }

    return false
  }

  getUpdatedContentById(contentId: number) {
    // id로 접근할 때에는 필터링 조건을 사용하지 않음
    return this._updatedContents.find(({ id }) => Number(id) === contentId)
  }

  getStoredInfo(updatedContentId: number) {
    const updatedContentsInfoInLocalStorage = localStorageService.getItem('UPDATED_CONTENTS') ?? []

    if (updatedContentsInfoInLocalStorage) {
      const updatedContentInfo = updatedContentsInfoInLocalStorage.find(
        ({ id }) => id === updatedContentId,
      )

      if (updatedContentInfo) {
        return updatedContentInfo
      }
    }

    return null
  }

  updateStoredInfo(updatedContentId: number) {
    const targetUpdatedContent = this._updatedContents.find(({ id }) => id === updatedContentId)

    if (!targetUpdatedContent || isNaN(Number(targetUpdatedContent.maxViewsPerDevice))) return

    const _storedInfo = localStorageService.getItem('UPDATED_CONTENTS') ?? [
      { id: updatedContentId, showCount: 0, version: 'V1' },
    ]

    const updatedContentInfo = _storedInfo.find(({ id }) => id === updatedContentId)

    if (updatedContentInfo) {
      updatedContentInfo.showCount++
      localStorageService.setItem('UPDATED_CONTENTS')(_storedInfo)
    }
  }
}

export const updatedContentsService = new UpdatedContentsService()
